import React, { useState, useEffect, Fragment, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactModal from 'react-modal';
import { joiResolver } from '@hookform/resolvers/joi';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import _debounce from 'lodash/debounce';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import moment from 'moment';

import { ConfirmModal, ActionButton } from '../../../common';
import {translationRuEnSameLetters, createArrayYearsFromNowToPast, permissionsAccess} from '../../../../helpers/common';
import {thingsSchema, carsEditSchema, puritiesSchema} from './editModalSchema';
import {HInput, HTextarea, DSelect, HRadio, HDatePicker, HCheckbox} from '../../../../validation/HForm';
import { resetSearch } from '../../../../redux/actions/searchAction';
import {
  requestForCalculation,
  resetCalculations,
} from '../../../../redux/actions/quickCalculationAction';
import { changePositionsContract, clearModalError } from '../../../../redux/actions/contractAction';
import { cashBoxService } from '../../../../services/cashbox-service';
import { Message } from '../../../utilites';
import Loader from './Loader/Loader';
import DeleteIcon from '../../../../assets/icons/trash.svg';

import './editDealModal.scss';
import {searchService} from "../../../../services/search-service";
import {messages} from "../../../../helpers/messages";
import BasicAlert from "../../../utilites/Alert/Alert";
import {config} from "../../../../helpers/config";

const years = createArrayYearsFromNowToPast(1970).map((i) => ({ value: i, label: i }));

function typeData (type, data, values) {
  switch (type) {
    case 'PURITY':
      data.positions = values.positions.map(({names, grossWeight, pureWeight, comment, showComment, positionId, purityId, quantity, additionalInfo, purityName, purityType, estimatedValue, prevPositionId }) => ({
        comment: showComment ? comment : '',
        grossWeight: +grossWeight,
        names,
        positionId: +positionId,
        pureWeight: +pureWeight,
        purityId: +purityId,
        quantity: +quantity,
        additionalInfo: additionalInfo,
        purityName: purityName,
        purityType: purityType,
        estimatedValue: estimatedValue,
        prevPositionId: prevPositionId,
      }))
      break;
    case 'THINGS':
      data.positions = values.positions.map(({ imei, model, names, positionId, quantity, serialNumber, additionalInfo }) => ({
        positionId: +positionId,
        quantity: +quantity,
        imei,
        model,
        names,
        serialNumber,
        additionalInfo: additionalInfo === null ? '' : additionalInfo,
      }))
      break;
    case 'CARS':
      data.positions = values.positions.map((i) => ({
        positionId: +i.positionId,
        names: [ i.makeOfCar, ],
        model: i.model,
        carDate: +i.year.value,
        registrationNumber: i.carNumber,
        quantity: +i.quantity,
        marketPrice: +i.marketPrice,
        estimatedValue: +i.estimatedPrice,
        certificateNumber: i.registrationNumber,
        certificateDate: moment(i.registrationDate).utc().format('YYYY-MM-DD'),
        vin: i.VIN,
        stateOFTheVehicle: i.condition,
        storageInCashBox: i.garage === 'C хранением в ломбарде',
        comment: '',
      }))
      break;
    default:
      break;
  }
}

const EditDealModal = memo(({ clientContract, handleCloseModal, type, isOpen, initValues }) => {
  const { isLoading, commonError } = useSelector(({ search }) => search);
  const { calculations, commonError: calculationError } = useSelector(({ quickCalculation }) => quickCalculation);
  const { modalError, isModalLoading, isModalResponse } = useSelector(({ contract }) => contract);
  const { accessFeatures } = useSelector(({ user }) => user);
  const [errorModalState, setErrorModalState] = useState(false);

  const [state, setState] = useState(_cloneDeep(clientContract));
  const [cars, setCars] = useState([]);
  const [carModels, setCarModels] = useState({});
  const [selectedMakeOfCar, setSelectedMakeOfCar] = useState('');
  const [confirmOpen, setConfirmOpen] = useState({
    isDismissButton: false,
    isApplyButton: false,
    data: {},
  });
  const [jewel, setJewel] = useState([]);
  const [part, setPart] = useState([]);
  const [charCounts, setCharCounts] = useState({})
  const dispatch = useDispatch();
  const notEmpty = state.positions && state.positions.length !== 0;
  const combineError = commonError || calculationError || modalError;

  const schema = {
    CARS: carsEditSchema,
    THINGS: thingsSchema,
    PURITY: puritiesSchema(config().validatePurities)
  }
  useEffect(() => {
    searchService.searchJewels('', 1000).then((response) => {
      setJewel(
        response.data.results.map((v) => ({
          value: parseInt(v.id),
          label: v.name,
          part: v.part,
        })),
      );
      setPart(
        response.data.results.map((v) => ({
          value: parseInt(v.id),
          label: v.part,
        })),
      );
    })
      .catch(error => {
        if (error.response) {
          let errorData = error.response.data
          let commonError = messages["0"]
          if (errorData.errorCode) {
            commonError = messages[errorData.errorCode]
          }
          throw commonError
        }
      })
  }, []);

  const filterNames = (arr) => {
    const filterArr = arr.filter(n => n !== 'ч.' && n !== 'б/вст' && n !== 'с/вст' && n !== '/вставка')
    if (filterArr?.length > 1) {
      return {label: filterArr.join(','), value: 0}
    }
    const filterJewel = jewel && jewel.filter(j => j.label === filterArr[0] || j.part === filterArr[0])
      .map((i) => {
          return {value: i.value, label: i.label}
        }
      )
    return {value: filterJewel[0]?.value, label: filterArr[0]}
  }

  const form = useForm({
    mode: 'all',
    defaultValues: {
      positions:
        notEmpty &&
        state.positions.map((position) => {
          let fields;
          switch (type) {
            case 'PURITY':
              fields = {
                comment: position.comment,
                grossWeight: position.grossWeight,
                names: filterNames(position.names),
                pureWeight: position.pureWeight,
                quantity: position.quantity,
                positionId: position.positionId,
                purityId: position.purityId,
                purityPrice: position.purityPrice,
                isPart: position.names.includes('ч.'),
                additionalInfo: position.additionalInfo == null ? '' : position.additionalInfo,
                showComment: !!position.comment
              }
              break;
            case 'THINGS':
              fields = {
                imei: position.imei,
                model: position.model,
                names: position.names[0],
                quantity: position.quantity,
                serialNumber: position.serialNumber,
                positionId: position.positionId,
                estimatedValue: state.totalEstimatedValue,
                additionalInfo: position.additionalInfo == null ? '' : position.additionalInfo
              };
              break;
            case 'CARS':
              fields = {
                makeOfCar: position.names[0],
                model: position.model,
                year: position.carDate,
                carNumber: position.registrationNumber,
                quantity: position.quantity,
                estimatedPrice: position.estimatedValue,
                marketPrice: position.marketPrice,
                registrationNumber: position.certificateNumber,
                registrationDate: position.certificateDate,
                VIN: position.vin,
                garage: position.storageInCashBox,
                condition: position.stateOFTheVehicle,
                positionId: position.positionId,
              };
              break;
            default:
              break;
          }
          return fields
        }),
    },
    resolver: joiResolver(schema[type])
  });
  const {append, remove, ...fieldArray} = useFieldArray({
    control: form.control,
    name: 'positions',
    keyName: 'positionIds',
  });

  useEffect(() => {
    if (type === "CARS") {
      loadCars()
      setSelectedMakeOfCar(fieldArray.fields[0].makeOfCar.toLowerCase())
    }
  }, [fieldArray.fields, type])

  const loadCars = () => {
    cashBoxService.getCars('')
      .then((response) => {
        setCars(response.data.results.map((v) => ({
                  value: v.brand,
                  label: v.brand,
                })))
        let modelsByCarName = response.data.results.reduce(function(acc, cur) {
          let carModels = {
            brand: cur.brand,
            models: cur.model.map((i) => ({
              value: i,
              label: i,
            }))
          }
          acc[cur.brand.toLowerCase()] = carModels
          return acc;
        }, {});
        setCarModels(modelsByCarName)
      })
  }

  const handleCarModelChange = () => {
    const values = form.getValues()

    form.setValue('positions[0].model', {value: '', label: ''})
    const currentMakeOfCar = values.positions[0].makeOfCar.label.toLowerCase();
    setSelectedMakeOfCar(currentMakeOfCar)
  };



  useEffect(() => {
    if (calculations && calculations.estimatedValue.length) {
      setState((prev) => ({
        ...prev,
        positions: prev.positions.map((i, ind) => ({
          ...i,
          estimatedValue: calculations.estimatedValue[ind],
        })),
        totalEstimatedValue: +calculations.totalEstimatedValue.toFixed(2),
      }));
    }
  }, [calculations]);

  useEffect(() => {
    return () => {
      dispatch(resetCalculations());
      dispatch(resetSearch());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isModalResponse) {
      handleCloseModal('isEdit', true);
    }
  }, [isModalResponse, handleCloseModal, dispatch]);

  const onSubmit = (values) => {
    if (!_isEqual({}, values)) {
      let dataCheck = {}

      typeData(type, dataCheck, values)

      const check =
        (type === 'THINGS' && +values.positions[0].estimatedValue !== state.totalEstimatedValue)
        || (type === 'CARS' && +values.positions[0].estimatedPrice !== state.totalEstimatedValue)

      if (!_isEqual(initValues, dataCheck) || check) {
        let data;
        switch (type) {
          case 'PURITY':
            data = {
              dealId: state.dealId,
            positionBeans: values.positions.map((i, index) => ({
            type: 'PURITY',
            positionId: clientContract.positions.length===values.positions.length ? +i.positionId : index < clientContract.positions.length ? +i.positionId : null,
            names: [i.names.label],
            isPart: i.isPart,
            quantity: +i.quantity,
            purityId: +i.purityId,
            grossWeight: +i.grossWeight,
            pureWeight: +i.pureWeight,
            purityPrice: +i.purityPrice,
            additionalInfo: i.additionalInfo == null ? '' : i.additionalInfo,
            comment: i.showComment ? i.comment : '',
            purityName: +i.purityName,
            purityType: i.purityType,
            prevPositionId: i.prevPositionId ? + i.prevPositionId : null,
            estimatedValue: i.estimatedValue,
          })),
        }
            break;
          case 'THINGS':
            data = {
              dealId: state.dealId,
              positionBeans: values.positions.map((i) => ({
                type: 'THINGS',
                ...i,
                positionId: +i.positionId,
                names: [i.names],
                quantity: +i.quantity,
                serialNumber: i.serialNumber.toUpperCase(),
                estimatedValue: +i.estimatedValue,
                comment: '',
                imei: i.imei.toUpperCase(),
                model: i.model,
                additionalInfo: i.additionalInfo == null ? '' : i.additionalInfo,
              })),
            }
            break;
          case 'CARS':
            data = {
              dealId: state.dealId,
              positionBeans: values.positions.map((i) => ({
                type: 'CARS',
                positionId: i.positionId,
                names: [ i.makeOfCar.label, ],
                model: i.model.label,
                carDate: +i.year.value,
                registrationNumber: i.carNumber,
                quantity: i.quantity,
                marketPrice: +i.marketPrice,
                estimatedValue: +i.estimatedPrice,
                certificateNumber: i.registrationNumber,
                certificateDate: moment(i.registrationDate).utc().format('YYYY-MM-DD'),
                vin: i.VIN,
                stateOFTheVehicle: i.condition.trim(),
                storageInCashBox: i.garage === 'C хранением в ломбарде',
                comment: '',
              })),
            };
            break;
          default:
            break;
        }

        setConfirmOpen((prev) => ({ ...prev, isApplyButton: true, data }));
      }
    }
  };

  const purityChange = () => {
    const data = form.getValues();
    const req = {
      type: 'PURITY',
      couponId: null,
      dealForm: clientContract.form,
      purities: data.positions.map(({ grossWeight, pureWeight, purityId, purityPrice, quantity }) => ({
        grossWeight: +grossWeight,
        pureWeight: +pureWeight,
        purityId: +purityId,
        purityPrice: +purityPrice,
        quantity: +quantity,
      })),
    };
    dispatch(requestForCalculation(req));
    form.setValue('positions', data.positions);
  };

  const debounce = _debounce(purityChange, 100);

  const handlePureWeight = (idx, decimalPlaces) => {
    const data = form.getValues().positions[idx];
    const pureWeight = data.pureWeight;
    const grossWeight = data.grossWeight;
    const newGrossWeight = +pureWeight > +grossWeight ? pureWeight : grossWeight;

    form.setValue(`positions[${idx}].grossWeight`, newGrossWeight, { shouldValidate: true });
    if ( +pureWeight < 0
      && +grossWeight > 0
      && (+grossWeight) >= (+pureWeight) )
    {
      let newPureWeight = ((+grossWeight) + (+pureWeight)).toFixed(decimalPlaces).replace(/\.?0+$/, "")
      newPureWeight = newPureWeight > 0 ? newPureWeight : 0
      form.setValue(`positions[${idx}].pureWeight`, newPureWeight, { shouldValidate: true });
    }
    purityChange();
  };

  const handleOpenClose = useCallback(() => {
    const data = form.getValues();

    typeData(type, data, data)

    if (_isEqual(initValues, data)) {
      handleCloseModal('isEdit');
      dispatch(clearModalError());
    } else {
      setConfirmOpen((prev) => ({ ...prev, isDismissButton: true }));
    }
  }, [form, handleCloseModal, initValues, type, dispatch]);

  const handleLocalCloseModal = useCallback(() => {
    setConfirmOpen((prev) => ({ ...prev, isDismissButton: false, isApplyButton: false }));
  }, []);

  const closePopUp = useCallback(() => {
    handleCloseModal('isEdit');
    dispatch(clearModalError())
    setConfirmOpen((prev) => ({ ...prev, isDismissButton: false, isApplyButton: false }));
  }, [handleCloseModal, dispatch]);

  const applyChanges = useCallback(() => {
    dispatch(changePositionsContract(confirmOpen.data, clientContract.dealId));
    setConfirmOpen((prev) => ({ ...prev, isDismissButton: false, isApplyButton: false }));
  }, [confirmOpen.data, clientContract.dealId, dispatch]);

  const addPositionRow = () => {
    const field = form.getValues().positions

    const fieldPosition = state.positions[field.length - 1];
    const newDecimalPlaces = _cloneDeep(fieldPosition)
    newDecimalPlaces.prevPositionId = newDecimalPlaces.positionId
    newDecimalPlaces.positionId = null
    form.setValue(`positions[${field.length}]`, newDecimalPlaces)

    setState((prev) => ({
      ...prev, positions: [...prev.positions, newDecimalPlaces]
    }));
    const lastPosition = fieldArray.fields[fieldArray.fields.length - 1];
    const newPosition = _cloneDeep(lastPosition);
    newPosition.positionIds = Date.now();
    newPosition.prevPositionId = newPosition.positionId
    newPosition.positionId = null

    append(newPosition, false);
    debounce()
  }

  const checkValidLength = () => {
    if (accessFeatures['purities.count_contracts']) {
      if (fieldArray.fields.length < 10) {
        addPositionRow()
      } else {
        setErrorModalState(true);
      }
    } else {
      addPositionRow()
    }
  }

  const removePurityRow = (index) => {
    const data = form.getValues()
    let pos = [...data.positions];
    remove(index);
    pos.splice(index, 1);
    form.setValue('positions', pos)
    debounce()
  };

  const handleChangeContent = () => {
    const newPositions = form.getValues().positions;
    form.setValue('positions', newPositions);
  };

  const handlePart = (idx) => {
    if (idx !== undefined) {
      const data = form.getValues().positions[idx];
      const newIsPart =data.isPart;
      const newJewel = jewel?.filter(jewel => jewel.label === data.names?.label || jewel.part === data.names?.label).map(j => {
        if (newIsPart) {
          return {value: j.value, label: j.part}
        }
        return {value: j.value, label: j.label}
      })
      form.setValue(`positions[${idx}].names`, newJewel[0], {shouldValidate: true});

      handleChangeContent()
    }
  }

  const handleCharCountChange = (idx) => {
    if (!accessFeatures['count_additionalInfo_symbols']) {
      return;
    }

    const data = form.getValues();
    let additionalInfo = data.positions[idx].additionalInfo;

    let charCount = additionalInfo.length > 0 ? additionalInfo.length : 0;

    setCharCounts((prevCharCounts) => ({
      ...prevCharCounts,
      [idx]: charCount,
    }));
  };

  return (
    <ReactModal
      isOpen={isOpen}
      contentLabel="Modal"
      className="modal-container editModal-modal-container"
      overlayClassName="modal-overlay">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {combineError && <Message type="error" text={combineError} />}
          <div className="modal-header editModal-modal-header">
            <h2> Редактирование договора</h2>
          </div>
          <div className="modal-body editModal-modal-body">
            {isModalLoading ? (
              <Loader />
            ) : (
              <div className="content-datatable">
                <div className="content-datatable-content">
                  <table className="table hover">
                    <thead className="editModal-table-head">
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="editModal-table-body">
                      {type === 'PURITY' &&
                        fieldArray.fields.map((position, idx) => {
                          const fieldPosition = state.positions[idx];
                          const decimalPlaces = fieldPosition?.purityType === 'Серебро' ? 1 : 2;
                          return (
                            <Fragment key={position.positionIds}>
                              <tr className="x">
                                <td colSpan="2">
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        <span>{`${idx+1}`}</span> Наименование
                                      </label>
                                      <div className="form-element-data combobox-lg">
                                        <DSelect
                                          name={`positions[${idx}].names`}
                                          defaultValue={position.names}
                                          options={position.isPart ? part : jewel}
                                          maxMenuHeight={200}
                                          isSearchable={true}
                                          isLoading={isLoading}
                                          placeholder={"Название"}
                                          handleChangeContent={handleChangeContent}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].positionId`}
                                          defaultValue={position.positionId}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].purityId`}
                                          defaultValue={position.purityId}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].purityPrice`}
                                          defaultValue={position.purityPrice}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].purityName`}
                                          defaultValue={fieldPosition?.purityName}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].purityType`}
                                          defaultValue={fieldPosition?.purityType}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].estimatedValue`}
                                          defaultValue={fieldPosition?.estimatedValue?.toFixed(2)}
                                        />
                                        <HInput
                                          type="hidden"
                                          name={`positions[${idx}].prevPositionId`}
                                          defaultValue={fieldPosition?.prevPositionId}
                                        />

                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td colSpan="4">
                                  <div className="table-cellcontent">
                                    <div className="form-element feature-toggle-off">
                                      <div className="form-element__titleSpacer">
                                        {accessFeatures['purities.additional_info'] && (
                                            <label
                                                className="form-element-label"
                                                htmlFor="puritiesComment">
                                              Доп. описание
                                            </label>
                                        )}
                                        <button
                                            type="button"
                                            className={(idx >= clientContract.positions.length) ? "button-icontrigger" : 'hidden'}
                                            onClick={() => {
                                              if (idx >= clientContract.positions.length) {
                                                removePurityRow(idx);
                                              }
                                            }}
                                        >
                                            <span className="icon">
                                              <DeleteIcon/>
                                            </span>
                                        </button>
                                      </div>
                                      {accessFeatures['purities.additional_info'] && (
                                          <div className="form-element-data textarea ">
                                            <HTextarea
                                                className="input editModal-input"
                                                rows="1"
                                                name={`positions[${idx}].additionalInfo`}
                                                defaultValue={position.additionalInfo}
                                                onBlur={handleChangeContent}
                                                onChange={()=>handleCharCountChange(idx)}
                                            />
                                            {
                                                accessFeatures['count_additionalInfo_symbols'] &&
                                                <span>{charCounts[idx] !== undefined ? charCounts[idx] : position.additionalInfo.length} /20 </span>
                                            }
                                          </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              <tr className="x">
                                <td colSpan="2">
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <HCheckbox
                                        h="true"
                                        name={`positions[${idx}].isPart`}
                                        className="input"
                                        defaultValue={position?.isPart}
                                        onChange={()=>handlePart(idx)}
                                      />
                                    </div>
                                    <label className="form-element-label m-r-spacer" htmlFor="isPart">
                                      Часть
                                    </label>
                                  </div>
                                </td>
                                <td colSpan="4">
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <HCheckbox
                                        h="true"
                                        name={`positions[${idx}].showComment`}
                                        className="input"
                                        defaultValue={position.showComment}
                                        onChange={handleChangeContent}
                                      />
                                    </div>
                                    <label
                                      className="form-element-label m-r-spacer" htmlFor="comment">
                                      Вставка
                                    </label>
                                  </div>
                                  <div className="form-element">
                                    <div className={`form-element-data textarea full-width ${
                                      position?.showComment ? '' : 'd-none'
                                    }`}>
                                      <HTextarea
                                        className="input"
                                        name={`positions[${idx}].comment`}
                                        rows="1"
                                        defaultValue={position?.comment}
                                        onBlur={handleChangeContent}
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              <tr className="x">
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Проба
                                      </label>
                                      <p className="editModal-pos">
                                        {fieldPosition?.purityType} {fieldPosition?.purityName}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Цена за грамм
                                      </label>
                                      <p className="editModal-pos">
                                        {fieldPosition?.purityPrice} руб./г.
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Количество
                                      </label>
                                      <div className="form-element-data number">
                                        <HInput
                                          name={`positions[${idx}].quantity`}
                                          defaultValue={position.quantity}
                                          className="input"
                                          type="text"
                                          decimals={0}
                                          onBlur={debounce}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Общий вес, г.
                                      </label>
                                      <div className="form-element-data number">
                                        <HInput
                                          name={`positions[${idx}].grossWeight`}
                                          defaultValue={position.grossWeight}
                                          onBlur={debounce}
                                          className="input"
                                          type="text"
                                          decimals={decimalPlaces}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Лигатурный вес, г.
                                      </label>
                                      <div className="form-element-data number">
                                        <HInput
                                          name={`positions[${idx}].pureWeight`}
                                          defaultValue={position.pureWeight}
                                          onBlur={() => handlePureWeight(idx, decimalPlaces)}
                                          className="input"
                                          type="text"
                                          minus={true}
                                          decimals={decimalPlaces}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="table-cellcontent">
                                    <div className="form-element">
                                      <label
                                        className="form-element-label"
                                        htmlFor="puritiesComment">
                                        Оценочная стоимость
                                      </label>
                                      <p className="editModal-pos">
                                        {fieldPosition?.estimatedValue?.toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              <tr className="editModal-table-body"></tr>
                            </Fragment>
                          );
                        })
                      }
                      { type === 'THINGS' &&
                        fieldArray.fields.map((position, idx) => {
                              return (
                                <Fragment key={position.positionId}>
                                  <tr>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            Наименование
                                          </label>
                                          <div className="form-element-data combobox-md">
                                            <HInput
                                              name={`positions[${idx}].names`}
                                              defaultValue={position.names}
                                              className="input"
                                              type="text"
                                            />
                                            <HInput
                                              type="hidden"
                                              name={`positions[${idx}].positionId`}
                                              defaultValue={position.positionId}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            Модель
                                          </label>
                                          <div className="form-element-data input-sm">
                                            <HInput
                                              name={`positions[${idx}].model`}
                                              defaultValue={position.model}
                                              className="input"
                                              type="text"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            IMEI
                                          </label>
                                          <div className="form-element-data input-sm">
                                            <HInput
                                              name={`positions[${idx}].imei`}
                                              defaultValue={position.imei}
                                              className="input text-transform"
                                              type="text"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            S/N
                                          </label>
                                          <div className="form-element-data input-sm">
                                            <HInput
                                              name={`positions[${idx}].serialNumber`}
                                              defaultValue={position.serialNumber}
                                              className="input text-transform"
                                              type="text"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            Количество
                                          </label>
                                          <div className="form-element-data number">
                                            <HInput
                                              name={`positions[${idx}].quantity`}
                                              value={position.quantity}
                                              className="input__disabled"
                                              type="text"
                                              decimals={0}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="table-cellcontent">
                                        <div className="form-element">
                                          <label
                                            className="form-element-label"
                                            htmlFor="puritiesComment">
                                            Оценочная стоимость
                                          </label>
                                          <div className="form-element-data input-sm">
                                            <HInput
                                              name={`positions[${idx}].estimatedValue`}
                                              defaultValue={position.estimatedValue}
                                              className="input"
                                              type="text"
                                              decimals={2}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                  { accessFeatures['things.additional_info'] &&
                                      <tr>
                                      <td colSpan={12}>
                                        <div className="form-element-data">
                                          <label className="form-element-label m-r-spacer" htmlFor="showComment">
                                            Доп. описание
                                          </label>
                                          <HTextarea
                                              h="true"
                                              className="input full-width purities-additionalInformation"
                                              rows="1"
                                              cols="10"
                                              name={`positions[${idx}].additionalInfo`}
                                              defaultValue={position.additionalInfo}
                                              onChange={()=>handleCharCountChange(idx)}
                                          />
                                          {
                                              accessFeatures['count_additionalInfo_symbols'] &&
                                              <span>{charCounts[idx] !== undefined ? charCounts[idx] : position.additionalInfo.length} / 38</span>
                                          }
                                        </div>
                                      </td>
                                    </tr>
                                  }
                                </Fragment>
                              );
                            })
                      }
                      { type === 'CARS' &&
                        fieldArray.fields.map((position, idx) => {
                              return (
                                <Fragment key={position.positionId}>
                                  <tr>
                                    <th scope="col">Марка</th>
                                    <th scope="col">Модель</th>
                                    <th scope="col">Год выпуска</th>
                                    <th scope="col">Количество</th>
                                    <th scope="col">
                                      Оценочная
                                      <br />
                                      стоимость, руб.
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>
                                        <span className="caption">Марка</span>
                                        <div className="select__width">
                                            <DSelect
                                              h="true"
                                              name={`positions[${idx}].makeOfCar`}
                                              defaultValue={{label: position.makeOfCar, value: position.makeOfCar}}
                                              handleChangeContent={handleCarModelChange}
                                              options={cars}
                                              isSearchable={true}
                                              isLoading={false}
                                            />
                                            <HInput
                                              type="hidden"
                                              name={`positions[${idx}].positionId`}
                                              defaultValue={position.positionId}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Модель</span>
                                        <div className="select__width">
                                            <DSelect
                                              h="true"
                                              name={`positions[${idx}].model`}
                                              defaultValue={{label: position.model, value: position.model}}
                                              options={carModels[selectedMakeOfCar]?.models}
                                              isSearchable={true}
                                              isLoading={false}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Год выпуска</span>
                                        <div className="select__width">
                                            <DSelect
                                              h="true"
                                              defaultValue={{label: position.year, value: position.year}}
                                              name={`positions[${idx}].year`}
                                              options={years}
                                              isLoading={false}
                                              placeholder="Год выпуска"
                                              isSearchable = "true"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Количество</span>
                                          <div className="form-element-data number">
                                            <HInput
                                              name={`positions[${idx}].quantity`}
                                              value={position.quantity}
                                              className="input__disabled quantity__width"
                                              type="text"
                                              decimals={0}
                                            />
                                          </div>
                                    </td>
                                    <td>
                                        <span className="caption">
                                            Оценочная стоимость, руб.
                                        </span>
                                        <div className="form-element-data number">
                                            <HInput
                                              h="true"
                                              className="input"
                                              type="text"
                                              name={`positions[${idx}].estimatedPrice`}
                                              defaultValue={position.estimatedPrice}
                                              id="carsCost"
                                              decimals={2}
                                            />
                                        </div>
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="col">VIN номер</th>
                                    <th scope="col">
                                        Номер
                                        <br />
                                        гос. регистрации
                                    </th>
                                    <th scope="col">
                                        Номер
                                        <br />
                                        свид. регистрации
                                    </th>
                                    <th scope="col">
                                      Дата выдачи
                                      <br />
                                      свид. регистрации
                                    </th>
                                    <th scope="col">
                                      Рыночная
                                      <br />
                                      стоимость руб.
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>
                                        <span className="caption">VIN номер</span>
                                        <div className="form-element limitedWidth">
                                            <HInput
                                              h="true"
                                              className="input-content"
                                              type="text"
                                              name={`positions[${idx}].VIN`}
                                              defaultValue={position.VIN}
                                              id="VIN"
                                              onChange={(event) => translationRuEnSameLetters(event, form.setValue, `positions[${idx}].VIN`)}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Номер гос. регистрации</span>
                                        <div className="form-element limitedWidth">
                                            <HInput
                                              h="true"
                                              className="input-content"
                                              type="text"
                                              name={`positions[${idx}].carNumber`}
                                              defaultValue={position.carNumber}
                                              id="carNumber"
                                              onChange={(event) => translationRuEnSameLetters(event, form.setValue, `positions[${idx}].carNumber`)}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Номер свид. регистрации</span>
                                        <div className="form-element limitedWidth">
                                            <HInput
                                              h="true"
                                              className="input-content text-transform"
                                              type="text"
                                              name={`positions[${idx}].registrationNumber`}
                                              defaultValue={position.registrationNumber}
                                              id="registrationNumber"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">Дата выдачи свид. регистрации</span>
                                        <div className="form-element limitedWidth">
                                            <HDatePicker
                                              h="true"
                                              name={`positions[${idx}].registrationDate`}
                                              defaultValue={position.registrationDate}
                                              className="input-content"
                                              id="registrationDate"
                                              max={moment().utc().format('YYYY-MM-DD')}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="caption">
                                        Рыночная стоимость руб.
                                        </span>
                                        <div className="form-element-data number">
                                            <HInput
                                              h="true"
                                              className="input"
                                              type="text"
                                              name={`positions[${idx}].marketPrice`}
                                              defaultValue={position.marketPrice}
                                              id="marketPrice"
                                              decimals={2}
                                            />
                                        </div>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td colSpan={5}>
                                      <div className="form-element flex-direction-row m-none">
                                        <div className="form-element-data form-element-data__color">
                                          <HRadio h="true"
                                            labels={['Без хранения в ломбарде', 'C хранением в ломбарде']}
                                            defaultCheckedLabel={position.garage ? 'C хранением в ломбарде' : 'Без хранения в ломбарде'}
                                            name={`positions[${idx}].garage`}
                                            checkboxStyle={true}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                  <tr>
                                    <th colSpan={5} scope="col">Состояние ТС</th>
                                  </tr>
                                  <tr>
                                    <td colSpan={5}>
                                        <HTextarea
                                            h="true"
                                            className="input full-width"
                                            rows="3"
                                            name={`positions[${idx}].condition`}
                                            defaultValue={position.condition}
                                        />
                                    </td>
                                  </tr>
                                </Fragment>
                              );
                            })
                      }
                    </tbody>
                    {type === 'PURITY' && (
                      <tfoot className="editModal-table-foot">
                        <tr>
                          <td colSpan="5">
                            <p>Общая оценочная стоимость</p>
                          </td>
                          <td colSpan="1">
                            <p>{state.totalEstimatedValue}</p>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}
          </div>
          {type === 'PURITY' && permissionsAccess("deal.edit.add_item") ? (<div className="modal-footer clientContract-modal-footer">
              <ActionButton
                text="Добавить изделие"
                func={checkValidLength}
                styleName={isModalLoading || (accessFeatures['purities.count_contracts'] && fieldArray.fields.length >= 10)  ? "button-light" : "button"}
                disabled={isModalLoading}
              />
              <div>
                <ActionButton
                  text="Редактировать"
                  styleName="button"
                  disabled={isModalLoading}
                  submit
                />
                <ActionButton
                  text="Отменить"
                  func={handleOpenClose}
                  styleName="button-light"
                  disabled={isModalLoading}
                />
              </div>
            </div>) :
            (<div className="modal-footer clientContract-modal-footer">
              <ActionButton
                text="Редактировать"
                styleName="button"
                disabled={isModalLoading}
                submit
              />
              <ActionButton
                text="Отменить"
                func={handleOpenClose}
                styleName="button-light"
                disabled={isModalLoading}
              />
            </div>)
          }
          <ConfirmModal
            isOpen={confirmOpen.isDismissButton}
            handleClose={handleLocalCloseModal}
            handleAction={closePopUp}
            headerText="Отменить изменения договора?"
            bodyText="Внесённые данные не сохранятся."
          />
          <ConfirmModal
            isOpen={confirmOpen.isApplyButton}
            handleClose={handleLocalCloseModal}
            handleAction={applyChanges}
            headerText="Применить изменения?"
            bodyText="Внесённые данные сохранятся."
          />
          <BasicAlert onClose={() => setErrorModalState(false)}
                      message="Максимальное количество ценностей 10"
                      isOpen={errorModalState}
                      isError={true}
          />
        </form>
      </FormProvider>
    </ReactModal>
  );
});

export default EditDealModal;
